import React, { useRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { COLORS } from "../../../constants";
import ListViewCommon from "./ListViewCommon";
import SearchbarComponent from "../../../components/Searchbar/SearchbarComponent";
import { useBottomTabBarHeight } from "@react-navigation/bottom-tabs";
import { isMobileWeb } from "../../../utils/helper";
import CustomFab from "../../../components/CustomFAB/CustomFAB";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import FilterChips from "../../../components/FilterChips/FilterChips";

/**
 * ListView component renders a common list view for web.
 * It passes the FontAwesomeIcon component to ListViewCommon as a prop for rendering icons.
 *
 * @returns {JSX.Element} The rendered ListView component for the web.
 */
const ListView = ({ route }) => {
  const bottomTabBarHeight = useBottomTabBarHeight();
  const mobile = isMobileWeb();
  const containerRef = useRef(null);
  const [showFab, setShowFab] = useState(false);

  useEffect(() => {
    if (!mobile || !containerRef.current) return;

    const handleScroll = () => {
      const scrollTop = containerRef.current.scrollTop;
      setShowFab(scrollTop > 500);
    };

    const node = containerRef.current;
    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, [mobile]);

  return (
    <div
      ref={containerRef}
      style={{
        ...styles.container,
        height: `calc(100vh - ${bottomTabBarHeight}px)`,
      }}
    >
      <div
        style={{
          ...styles.stickyHeader,
          position: mobile ? "relative" : "sticky",
        }}
      >
        <div style={styles.searchContainer}>
          <SearchbarComponent FontAwesomeIcon={FontAwesomeIcon} size={"xl"} />
        </div>

        {!mobile && (
          <div style={styles.chipsContainer}>
            <FilterChips FontAwesomeIcon={FontAwesomeIcon} />
          </div>
        )}
      </div>

      {/* LIST AREA */}
      <div style={styles.listContent}>
        {/* Mobile: Chips inside scrollable list */}
        {mobile && (
          <div style={styles.chipsInList}>
            <FilterChips FontAwesomeIcon={FontAwesomeIcon} />
          </div>
        )}
        <ListViewCommon route={route} FontAwesomeIcon={FontAwesomeIcon} renderList />

        {/* Mobile-only FAB to scroll to top via navigation */}
        {mobile && showFab && (
          <CustomFab
            onFabClick={() => {
              containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
            FontAwesomeIcon={FontAwesomeIcon}
            icon={faArrowUp}
            size="lg"
            color={COLORS.textLight}
            bottomOffset={16}
            rightOffset={16}
            accessibilityLabel="Back to Top"
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: COLORS.backgroundColor,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    paddingBottom: 40,
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: COLORS.backgroundColor,
  },
  listContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
};

export default ListView;
